/**
 * Battle-command modal (v0M3.5, revised v0M3.5+fix).
 *
 * DOM structure is built ONCE. Per-frame updates mutate existing element
 * text / class / disabled state — never `innerHTML = ''`. This preserves
 * click handlers so the X button + order buttons actually fire.
 * Canvas keeps redrawing each frame (safe — no DOM click concerns).
 * The right panel's cohort section rebuilds ONLY when the primary selected
 * cohort id changes, then mutates values on subsequent frames. v0M4.0h.4:
 * multi-select via Shift-LMB — the Set holds all selected cohorts;
 * `firstSelected()` returns the primary for details rendering.
 */

import { injectMenuStyles } from './menu-styles';
import { getFactionById } from '../../data/factions';
import type { Command } from '../../sim/commands';
import type { BattleId, Battle, CohortOnField, CombatLogEvent, Side, StandingOrder, TerrainType } from '../../sim/battle-state';
import type { CohortId, FactionId, GameState } from '../../sim/state';
import { BATTLESCAPE_HEIGHT, BATTLESCAPE_WIDTH, TERRAIN_TYPE_ORDER } from '../../sim/battle-state';
import { processNewBattleEvents, renderBattleEffects, resetEffectTracking } from './battle-effects';
import { loadFormationTemplates } from '../../data/formation-templates';
import { loadKnowledge } from '../../data/knowledge';
import { buildFormationUnlockMap, isFormationUnlocked } from '../../sim/knowledge-effects';

// v0M9.14 (2026-07-24) — Static formation unlock map. Formations gated
// by knowledge (Strong Right Flank, Hammer and Anvil, Cannae at v0M9)
// show as "Locked" until researched.
const BC_KNOWLEDGE = loadKnowledge();
const FORMATION_UNLOCK_MAP = buildFormationUnlockMap(BC_KNOWLEDGE);

/**
 * v0M4.3.5 (2026-07-23) — Turn a formation-template sheet name (e.g.
 * "FormationBalanced") into a UI-friendly label ("Balanced"). Strips the
 * "Formation" prefix and inserts a space before each interior uppercase
 * letter so "FormationWideFront" → "Wide Front".
 */
function humanizeFormationName(name: string): string {
  const stripped = name.startsWith('Formation') ? name.slice('Formation'.length) : name;
  return stripped.replace(/([a-z])([A-Z])/g, '$1 $2');
}

const MIN_CELL_PX = 30;
const MAX_CELL_PX = 56;
const DEFAULT_CELL_PX = 44;

/**
 * Compute a cell size that fits both viewport width and height with room
 * for the modal chrome (header + left panel + right panel + padding).
 * The inset row lives below the grid — modal is scrollable so an
 * over-tall layout is handled by browser scroll, and the modal auto-
 * scrolls to reveal the inset when a cohort is selected.
 * Clamped to [MIN_CELL_PX, MAX_CELL_PX].
 */
function computeCellSize(): number {
  const viewportW = window.innerWidth || 1400;
  const viewportH = window.innerHeight || 900;
  const chromeW = 220 + 260 + 60;
  const chromeH = 90 + 40;
  const availW = viewportW - chromeW;
  const availH = viewportH - chromeH;
  const maxByW = Math.floor(availW / BATTLESCAPE_WIDTH);
  const maxByH = Math.floor(availH / BATTLESCAPE_HEIGHT);
  const raw = Math.min(maxByW, maxByH, MAX_CELL_PX);
  return Math.max(MIN_CELL_PX, isFinite(raw) ? raw : DEFAULT_CELL_PX);
}

export interface BattleCommandModalCallbacks {
  onIssue(cmd: Command): void;
  onClose(): void;
}

export interface BattleCommandModalHandle {
  sync(state: GameState, openBattleId: BattleId | null, playerFactionId: FactionId | null): void;
  destroy(): void;
}

export function mountBattleCommandModal(
  root: HTMLElement,
  callbacks: BattleCommandModalCallbacks,
): BattleCommandModalHandle {
  injectMenuStyles();

  const backdrop = document.createElement('div');
  backdrop.className = 'su-battle-backdrop';
  backdrop.style.display = 'none';

  const modal = document.createElement('div');
  modal.className = 'su-battle-modal';
  backdrop.appendChild(modal);

  // Persistent header structure — text mutated per frame.
  const header = document.createElement('div');
  header.className = 'su-battle-header';
  const vs = document.createElement('h2');
  vs.className = 'su-battle-vs';
  const phaseWrap = document.createElement('div');
  phaseWrap.className = 'su-battle-phase';
  const phaseName = document.createElement('span');
  const phaseTimer = document.createElement('span');
  phaseTimer.className = 'su-battle-phase-timer';
  phaseTimer.style.marginLeft = '8px';
  phaseWrap.appendChild(phaseName);
  phaseWrap.appendChild(phaseTimer);
  const closeBtn = document.createElement('button');
  closeBtn.className = 'su-city-panel-close';
  closeBtn.textContent = '×';
  closeBtn.title = 'Close (Esc)';
  closeBtn.addEventListener('click', () => callbacks.onClose());
  header.appendChild(vs);
  header.appendChild(phaseWrap);
  header.appendChild(closeBtn);
  modal.appendChild(header);

  // v0M4.1.3 — Phase strip under the header. 3 cells (Movement / Combat /
  // Resolution) plus an optional Setup cell shown only while phase1
  // (player takeCommand) is active. Currently-active cell highlighted;
  // per-phase progress bar underneath the active cell.
  const phaseStrip = document.createElement('div');
  phaseStrip.className = 'su-battle-phase-strip';
  const PHASE_STRIP_CELLS: ReadonlyArray<{ label: string; phase: 'setup' | 'movement' | 'combat' | 'resolution' }> = [
    { label: 'Setup', phase: 'setup' },
    { label: 'Movement', phase: 'movement' },
    { label: 'Combat', phase: 'combat' },
    { label: 'Resolution', phase: 'resolution' },
  ];
  const phaseStripCells: Array<{ wrap: HTMLElement; label: HTMLElement; bar: HTMLElement; phase: 'setup' | 'movement' | 'combat' | 'resolution' }> = [];
  for (const cell of PHASE_STRIP_CELLS) {
    const wrap = document.createElement('div');
    wrap.className = 'su-phase-cell';
    wrap.dataset.phase = cell.phase;
    const label = document.createElement('div');
    label.className = 'su-phase-cell-label';
    label.textContent = cell.label;
    const barOuter = document.createElement('div');
    barOuter.className = 'su-phase-cell-bar-outer';
    const barFill = document.createElement('div');
    barFill.className = 'su-phase-cell-bar-fill';
    barOuter.appendChild(barFill);
    wrap.appendChild(label);
    wrap.appendChild(barOuter);
    phaseStrip.appendChild(wrap);
    phaseStripCells.push({ wrap, label, bar: barFill, phase: cell.phase });
  }
  modal.appendChild(phaseStrip);

  // Body — persistent 3-column layout.
  const body = document.createElement('div');
  body.className = 'su-battle-body';
  const leftPanel = document.createElement('div');
  leftPanel.className = 'su-battle-panel';

  // v0M4.1.1 — Combat log section at the top of the left panel.
  // Minimisable via chevron. Rows appended per frame from battle.log.
  const combatLogSection = document.createElement('div');
  combatLogSection.className = 'su-combat-log';
  const combatLogHeader = document.createElement('div');
  combatLogHeader.className = 'su-combat-log-header';
  const combatLogTitle = document.createElement('span');
  combatLogTitle.textContent = 'Combat log';
  const combatLogToggle = document.createElement('button');
  combatLogToggle.className = 'su-combat-log-toggle';
  combatLogToggle.textContent = '▾';
  combatLogToggle.title = 'Minimise combat log';
  combatLogHeader.appendChild(combatLogTitle);
  combatLogHeader.appendChild(combatLogToggle);
  combatLogSection.appendChild(combatLogHeader);
  const combatLogList = document.createElement('div');
  combatLogList.className = 'su-combat-log-list';
  combatLogSection.appendChild(combatLogList);
  let combatLogMinimised = false;
  let lastRenderedLogIndex = 0;
  let lastLoggedBattleId: number | null = null;
  let hoveringLog = false;
  combatLogList.addEventListener('mouseenter', () => { hoveringLog = true; });
  combatLogList.addEventListener('mouseleave', () => { hoveringLog = false; });
  combatLogToggle.addEventListener('click', () => {
    combatLogMinimised = !combatLogMinimised;
    combatLogList.style.display = combatLogMinimised ? 'none' : '';
    combatLogToggle.textContent = combatLogMinimised ? '▸' : '▾';
    combatLogToggle.title = combatLogMinimised ? 'Expand combat log' : 'Minimise combat log';
  });
  leftPanel.appendChild(combatLogSection);

  // v0M4.1.2 — Left panel is now Orders-only. Army stats + commander
  // display live in the right panel (see armySection below). Cohort order
  // buttons also live in the left panel per Michael Q2 lock.
  const leftTitle = document.createElement('h3');
  leftTitle.className = 'su-battle-panel-title';
  leftTitle.textContent = 'Orders';
  leftPanel.appendChild(leftTitle);

  // v0M4.0h.1: Formation picker — 4 auto-layout templates per §2.8.3.
  // Phase-1-only (disabled once combat starts, symmetric with Commander's
  // Orders' Phase-2 gate). Click emits set_battle_formation with the
  // player's own side index; applyFormation repositions cohorts per template.
  const formationLabel = document.createElement('div');
  formationLabel.className = 'su-battle-panel-label';
  formationLabel.textContent = 'Formation';
  leftPanel.appendChild(formationLabel);

  // v0M4.3.5 (2026-07-23) — formation button list is now driven by the
  // xlsx-loaded formation-template library. Each tab in BattleMap.xlsx
  // matching /^Formation(?!Template$)/ becomes a button. Retire the
  // hardcoded 4-template array (wide-front / strong-left-flank /
  // strong-right-flank / square).
  const formationButtons: Array<{ btn: HTMLButtonElement; template: string }> = [];
  const formationLibrary = loadFormationTemplates();
  const FORMATION_SPECS: ReadonlyArray<{ label: string; template: string }> = Object.keys(formationLibrary)
    .sort()
    .map((name) => ({ label: humanizeFormationName(name), template: name }));

  // v0M4.4.1 (2026-07-23) — formation-preview canvas rendered below the
  // formation buttons. Renders the currently-persisted-or-hovered
  // template's slot layout on a mini 20×20 grid, colour-coded by tag
  // (A grey / M red / R blue / C yellow / S green). Persisted selection
  // starts at the first (alphabetically-sorted) template so the panel
  // always shows something meaningful.
  let persistedTemplate: string | null = FORMATION_SPECS[0]?.template ?? null;

  for (const spec of FORMATION_SPECS) {
    const btn = document.createElement('button');
    btn.className = 'su-battle-order-btn';
    btn.textContent = spec.label;
    btn.addEventListener('mouseenter', () => {
      renderFormationPreview(spec.template);
    });
    btn.addEventListener('mouseleave', () => {
      renderFormationPreview(persistedTemplate);
    });
    btn.addEventListener('click', () => {
      if (!currentBattle || currentPlayerFactionId === null) return;
      let playerSideIndex: 0 | 1 | null = null;
      for (let s = 0; s < currentBattle.sides.length; s++) {
        const side = currentBattle.sides[s]!;
        for (const cof of side.cohorts.values()) {
          if (cof.factionId === currentPlayerFactionId) {
            playerSideIndex = s as 0 | 1;
            break;
          }
        }
        if (playerSideIndex !== null) break;
      }
      if (playerSideIndex === null) return;
      persistedTemplate = spec.template;
      renderFormationPreview(spec.template);
      callbacks.onIssue({
        type: 'set_battle_formation',
        tick: currentTick,
        playerFactionId: currentPlayerFactionId,
        battleId: currentBattle.id,
        sideIndex: playerSideIndex,
        template: spec.template,
      });
    });
    leftPanel.appendChild(btn);
    formationButtons.push({ btn, template: spec.template });
  }

  // Preview canvas — 20×20 grid @ 6 px/cell = 120 px square.
  const previewCanvas = document.createElement('canvas');
  previewCanvas.className = 'su-formation-preview';
  previewCanvas.width = 120;
  previewCanvas.height = 120;
  previewCanvas.style.marginTop = '6px';
  previewCanvas.style.border = '1px solid #4a3d24';
  previewCanvas.style.background = 'rgba(20, 16, 10, 0.5)';
  previewCanvas.style.imageRendering = 'pixelated';
  leftPanel.appendChild(previewCanvas);

  function renderFormationPreview(templateName: string | null): void {
    const ctx = previewCanvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    if (templateName === null) return;
    const template = formationLibrary[templateName];
    if (!template) return;
    const cellPx = previewCanvas.width / 20;
    // Faint grid lines.
    ctx.strokeStyle = 'rgba(74, 61, 36, 0.4)';
    ctx.lineWidth = 1;
    for (let i = 1; i < 20; i++) {
      ctx.beginPath();
      ctx.moveTo(i * cellPx, 0);
      ctx.lineTo(i * cellPx, previewCanvas.height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * cellPx);
      ctx.lineTo(previewCanvas.width, i * cellPx);
      ctx.stroke();
    }
    // Slot fill by tag.
    const TAG_COLOURS: Record<string, string> = {
      A: 'rgba(160, 160, 160, 0.9)',
      M: 'rgba(196, 116, 116, 0.9)',
      R: 'rgba(116, 168, 212, 0.9)',
      C: 'rgba(220, 200, 116, 0.9)',
      S: 'rgba(140, 196, 140, 0.9)',
    };
    for (const slot of template.slots) {
      ctx.fillStyle = TAG_COLOURS[slot.tag] ?? 'rgba(200, 200, 200, 0.9)';
      ctx.fillRect(slot.col * cellPx, slot.row * cellPx, cellPx, cellPx);
    }
  }

  // Initial render.
  renderFormationPreview(persistedTemplate);

  // MFix5+fix4 (2026-07-23): "End Deployment" button — player-elective
  // early-end of Setup. Enabled only during Phase 1; disabled + hidden
  // (via the collapse below) once Setup ends. Fires `end_setup_phase`,
  // which sets `battle.phase1EndTick = state.tick` and lets battles.ts
  // transition to Movement on the next tick.
  const endDeploymentBtn = document.createElement('button');
  endDeploymentBtn.className = 'su-battle-order-btn';
  endDeploymentBtn.textContent = 'End Deployment';
  endDeploymentBtn.style.marginTop = '6px';
  endDeploymentBtn.style.background = '#3a6a3a';
  endDeploymentBtn.style.borderColor = '#6aaa6a';
  endDeploymentBtn.addEventListener('click', () => {
    if (!currentBattle || currentPlayerFactionId === null) return;
    callbacks.onIssue({
      type: 'end_setup_phase',
      tick: currentTick,
      playerFactionId: currentPlayerFactionId,
      battleId: currentBattle.id,
    });
  });
  leftPanel.appendChild(endDeploymentBtn);

  // v0M4.0h.2: Commander's Orders — army-level cascade buttons per §2.8.2.
  // Phase-2-only (disabled during Phase 1 Setup per v0M4.0d apply-commands
  // gate). Click emits set_commander_order with the player's own side index.
  const commanderOrdersLabel = document.createElement('div');
  commanderOrdersLabel.className = 'su-battle-panel-label';
  commanderOrdersLabel.textContent = "Commander's Orders";
  leftPanel.appendChild(commanderOrdersLabel);

  // Order buttons: label + CommanderOrder tag.
  const commanderOrderButtons: Array<{ btn: HTMLButtonElement; order: 'full-assault' | 'stand-ground' | 'cautious-assault' | 'orderly-retreat' | 'withdraw-with-haste' }> = [];
  const COMMANDER_ORDER_SPECS: ReadonlyArray<{ label: string; order: 'full-assault' | 'stand-ground' | 'cautious-assault' | 'orderly-retreat' | 'withdraw-with-haste' }> = [
    { label: 'Full Assault', order: 'full-assault' },
    { label: 'Stand Ground', order: 'stand-ground' },
    { label: 'Cautious Assault', order: 'cautious-assault' },
    { label: 'Orderly Retreat', order: 'orderly-retreat' },
    { label: 'Withdraw with Haste', order: 'withdraw-with-haste' },
  ];
  for (const spec of COMMANDER_ORDER_SPECS) {
    const btn = document.createElement('button');
    btn.className = 'su-battle-order-btn';
    btn.textContent = spec.label;
    btn.addEventListener('click', () => {
      if (!currentBattle || currentPlayerFactionId === null) return;
      // Determine which side the player owns — the player is a participant
      // on exactly one side per v0M3.5 enter_battle_command gate.
      let playerSideIndex: 0 | 1 | null = null;
      for (let s = 0; s < currentBattle.sides.length; s++) {
        const side = currentBattle.sides[s]!;
        for (const cof of side.cohorts.values()) {
          if (cof.factionId === currentPlayerFactionId) {
            playerSideIndex = s as 0 | 1;
            break;
          }
        }
        if (playerSideIndex !== null) break;
      }
      if (playerSideIndex === null) return;
      callbacks.onIssue({
        type: 'set_commander_order',
        tick: currentTick,
        playerFactionId: currentPlayerFactionId,
        battleId: currentBattle.id,
        sideIndex: playerSideIndex,
        order: spec.order,
      });
    });
    leftPanel.appendChild(btn);
    commanderOrderButtons.push({ btn, order: spec.order });
  }

  const canvasWrap = document.createElement('div');
  canvasWrap.className = 'su-battle-canvas-wrap';
  const canvas = document.createElement('canvas');
  canvas.className = 'su-battle-canvas';
  // Cell size is computed at mount time from the current viewport so the
  // battlescape fits without scrolling on typical playtest screens.
  const cellPx = computeCellSize();
  const canvasW = BATTLESCAPE_WIDTH * cellPx;
  const canvasH = BATTLESCAPE_HEIGHT * cellPx;
  canvas.width = canvasW;
  canvas.height = canvasH;
  canvas.style.width = `${canvasW}px`;
  canvas.style.height = `${canvasH}px`;
  canvasWrap.appendChild(canvas);

  const rightPanel = document.createElement('div');
  rightPanel.className = 'su-battle-panel su-battle-panel-right';

  // v0M4.1.2 — Army stats section at the top of the right panel.
  // Shows Commander (name + rank + XP progress bar + cohort limit) plus
  // aggregate stats (cohort count / total figures / avg stamina / avg
  // morale / broken count). Updated per frame in `updateArmyPanel`.
  const armyPanelSection = document.createElement('div');
  armyPanelSection.className = 'su-army-section';
  const armyTitle = document.createElement('h3');
  armyTitle.className = 'su-battle-panel-title';
  armyTitle.textContent = 'Your Army';
  armyPanelSection.appendChild(armyTitle);
  const armyCommanderLine = document.createElement('div');
  armyCommanderLine.className = 'su-battle-panel-line';
  armyCommanderLine.dataset.slot = 'commander';
  armyPanelSection.appendChild(armyCommanderLine);
  const armyXpBarOuter = document.createElement('div');
  armyXpBarOuter.className = 'su-army-xp-bar-outer';
  const armyXpBarFill = document.createElement('div');
  armyXpBarFill.className = 'su-army-xp-bar-fill';
  armyXpBarOuter.appendChild(armyXpBarFill);
  armyPanelSection.appendChild(armyXpBarOuter);
  const armyXpLabel = document.createElement('div');
  armyXpLabel.className = 'su-battle-panel-line su-army-xp-label';
  armyXpLabel.dataset.slot = 'xp';
  armyPanelSection.appendChild(armyXpLabel);
  const armyAggregate = document.createElement('div');
  armyAggregate.className = 'su-battle-panel-line';
  armyAggregate.dataset.slot = 'aggregate';
  armyPanelSection.appendChild(armyAggregate);
  const armyRoster = document.createElement('div');
  armyRoster.className = 'su-battle-panel-line';
  armyRoster.dataset.slot = 'roster';
  armyPanelSection.appendChild(armyRoster);
  rightPanel.appendChild(armyPanelSection);

  const rightTitle = document.createElement('h3');
  rightTitle.className = 'su-battle-panel-title';
  rightTitle.textContent = 'Cohort';
  rightPanel.appendChild(rightTitle);
  const rightHint = document.createElement('div');
  rightHint.className = 'su-battle-panel-line';
  rightHint.style.color = '#a3906a';
  rightHint.textContent = 'Click any cohort (yours or enemy) to inspect its stats.';
  rightPanel.appendChild(rightHint);
  // Persistent cohort detail scaffold — hidden until a cohort is selected.
  const rightDetails = document.createElement('div');
  rightDetails.style.display = 'none';
  // MFix2.2 — 60-figure grid replaces the single large portrait. Each
  // figure renders as a small vertical bar with per-figure health tint:
  // green from bottom (healthy fraction) + orange (or red if wounded)
  // from top (damage fraction). Killed figures are hidden. Populated
  // in `updateRightPanel` from `cof.figures[i].health` per figure.
  // Retains the old portrait DOM elements as offscreen sentinels so
  // legacy update code that references them can be no-ops without
  // breaking (portrait / portraitIcon / portraitFallback referenced by
  // updateRightPanel's icon-probe code below).
  const figureGrid = document.createElement('div');
  figureGrid.className = 'su-battle-figure-grid';
  rightDetails.appendChild(figureGrid);
  const portrait = document.createElement('div');
  portrait.style.display = 'none';
  const portraitIcon = document.createElement('div');
  const portraitFallback = document.createElement('div');
  portrait.appendChild(portraitIcon);
  portrait.appendChild(portraitFallback);
  const rightName = document.createElement('div');
  rightName.className = 'su-battle-panel-line';
  rightName.style.fontWeight = 'bold';
  rightDetails.appendChild(rightName);
  const rightPos = document.createElement('div');
  rightPos.className = 'su-battle-panel-line';
  rightDetails.appendChild(rightPos);
  const rightMorale = document.createElement('div');
  rightMorale.className = 'su-battle-panel-line';
  rightDetails.appendChild(rightMorale);
  const rightStamina = document.createElement('div');
  rightStamina.className = 'su-battle-panel-line';
  rightDetails.appendChild(rightStamina);
  const rightFigures = document.createElement('div');
  rightFigures.className = 'su-battle-panel-line';
  rightDetails.appendChild(rightFigures);

  // v0M4.1.2 — extended cohort stat block per BATTLES.md §2.9.3.
  const rightTags = document.createElement('div');
  rightTags.className = 'su-battle-panel-line su-cohort-tags';
  rightDetails.appendChild(rightTags);
  const rightFacing = document.createElement('div');
  rightFacing.className = 'su-battle-panel-line';
  rightDetails.appendChild(rightFacing);
  const rightStandingOrder = document.createElement('div');
  rightStandingOrder.className = 'su-battle-panel-line';
  rightDetails.appendChild(rightStandingOrder);
  const balanceSheetLabel = document.createElement('div');
  balanceSheetLabel.className = 'su-battle-panel-label';
  balanceSheetLabel.textContent = 'Stats';
  rightDetails.appendChild(balanceSheetLabel);
  const rightStats = document.createElement('div');
  rightStats.className = 'su-battle-panel-stats';
  rightDetails.appendChild(rightStats);

  const ordersLabel = document.createElement('div');
  ordersLabel.className = 'su-battle-panel-label';
  ordersLabel.textContent = 'Orders';
  rightDetails.appendChild(ordersLabel);

  // MFix7.5 (2026-07-23) — individual cohort-order buttons removed per
  // Michael's playtest ask. Cohort orders now come exclusively from
  // Commander's Orders cascade (§2.8.2) or tag-based defaults. The
  // right panel keeps the Orders label as a read-only heading; the
  // cohort's currently-active order is still shown in the stats area
  // above so the player has context for behaviour.
  const cohortOrderButtons: Array<{
    btn: HTMLButtonElement;
    spec: { readonly label: string; readonly order: StandingOrder; readonly tag: 'universal' | 'melee' | 'ranged' };
  }> = [];
  const moveTargetLine = document.createElement('div');
  moveTargetLine.className = 'su-battle-panel-line';
  moveTargetLine.style.color = '#f4c584';
  moveTargetLine.style.marginTop = '12px';
  rightDetails.appendChild(moveTargetLine);
  rightPanel.appendChild(rightDetails);

  body.appendChild(leftPanel);
  body.appendChild(canvasWrap);
  body.appendChild(rightPanel);
  modal.appendChild(body);

  // ---------------------------------------------------------------------------
  // Cohort inset row (v0M3.5c) — full-width row below the grid showing all
  // 60 individual figures of the selected cohort at large size, with
  // per-figure alive/wounded/killed state. DOM structure built ONCE per
  // cohort selection; per-frame updates mutate background-color + visibility
  // only (no innerHTML rebuild — preserves any future click handlers here).
  // Figure size scales with the tactical cell size (1.4×, clamped) so a
  // small-viewport user still gets a proportionally larger inset than
  // their grid.
  // ---------------------------------------------------------------------------
  // MFix2.2 — Bottom inset row hidden permanently. The 60 figures moved
  // to the right-panel figureGrid at smaller size per Michael's playtest
  // request. DOM structure retained (dead but harmless) so the existing
  // updateInsetResult call path stays no-op.
  const inset = document.createElement('div');
  inset.className = 'su-battle-inset';
  inset.style.display = 'none';
  // Do NOT modal.appendChild(inset) below — MFix2.2 removes the DOM
  // mount entirely. (The `inset.style.display = 'none'` above is a
  // defensive backstop in case future code re-mounts it.)
  const insetTitle = document.createElement('div');
  insetTitle.className = 'su-battle-inset-title';
  const insetTitleName = document.createElement('span');
  insetTitleName.className = 'su-battle-inset-name';
  const insetTitleCounts = document.createElement('span');
  insetTitleCounts.className = 'su-battle-inset-counts';
  insetTitle.appendChild(insetTitleName);
  insetTitle.appendChild(insetTitleCounts);
  inset.appendChild(insetTitle);
  const insetFormation = document.createElement('div');
  insetFormation.className = 'su-battle-inset-formation';
  inset.appendChild(insetFormation);
  // MFix2.2 — bottom inset removed from the modal; figures relocated to
  // the right-panel figureGrid. Line kept as reference:
  // modal.appendChild(inset);

  // Compute the inset figure size once per mount from the tactical cell size.
  // Cap at 56 px so a 12-wide row (~720 px) still fits the min-width 1000 modal.
  const INSET_FIGURE_PX = Math.max(40, Math.min(56, Math.floor(cellPx * 1.15)));

  root.appendChild(backdrop);

  // Local UI state
  // v0M4.0h.4: multi-select. Empty set = no selection; size 1 = single
  // selection (LMB); size >1 = multi-select (Shift-LMB additions).
  const selectedCohortIds: Set<CohortId> = new Set();
  function firstSelected(): CohortId | null {
    for (const id of selectedCohortIds) return id;
    return null;
  }
  let currentBattle: Battle | null = null;
  let currentPlayerFactionId: FactionId | null = null;
  let currentTick = 0;
  let concludedNoticeSent = false;
  // Inset per-cohort DOM cache — rebuilt only when firstSelected() flips.
  let insetCohortId: CohortId | null = null;
  let insetFigureDivs: HTMLDivElement[] = [];

  // Canvas click routing — left-select, right-move.
  canvas.addEventListener('mousedown', (e) => {
    const rect = canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const cx = Math.floor(px / cellPx);
    const cy = Math.floor(py / cellPx);
    if (cx < 0 || cx >= BATTLESCAPE_WIDTH || cy < 0 || cy >= BATTLESCAPE_HEIGHT) return;
    if (e.button === 0) {
      const battle = currentBattle;
      if (!battle) return;
      let picked: CohortId | null = null;
      for (const side of battle.sides) {
        for (const cof of side.cohorts.values()) {
          if (cof.position.x === cx && cof.position.y === cy && !cof.broken) {
            picked = cof.id;
            break;
          }
        }
        if (picked !== null) break;
      }
      // v0M4.0h.4: LMB replaces selection; Shift-LMB toggles (adds if not
      // in set, removes if already in set). LMB on empty square clears
      // selection (picked === null).
      if (e.shiftKey && picked !== null) {
        if (selectedCohortIds.has(picked)) selectedCohortIds.delete(picked);
        else selectedCohortIds.add(picked);
      } else {
        selectedCohortIds.clear();
        if (picked !== null) selectedCohortIds.add(picked);
      }
    } else if (e.button === 2) {
      e.preventDefault();
      if (!currentBattle || currentPlayerFactionId === null) return;
      // v0M4.0h.4: RMB (move) requires exactly-1 selection. Multi-select
      // move (formation-preserving) is a PLAYTEST OPEN / v0M4.0h.4+fix
      // candidate. When multi-selected, RMB is a no-op.
      if (selectedCohortIds.size !== 1) return;
      const onlyId = firstSelected();
      if (onlyId === null) return;
      const cof = findCohortInBattle(currentBattle, onlyId);
      if (!cof || cof.factionId !== currentPlayerFactionId) return;
      callbacks.onIssue({
        type: 'set_cohort_move',
        tick: currentTick,
        playerFactionId: currentPlayerFactionId,
        battleId: currentBattle.id,
        cohortId: onlyId,
        targetSquare: { x: cx, y: cy },
      });
    }
  });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());

  function sync(state: GameState, openBattleId: BattleId | null, playerFactionId: FactionId | null): void {
    currentTick = state.tick;
    if (openBattleId === null || playerFactionId === null) {
      currentBattle = null;
      currentPlayerFactionId = null;
      backdrop.style.display = 'none';
      concludedNoticeSent = false;
      return;
    }
    const battle = state.battles.get(openBattleId);
    if (!battle) {
      if (!concludedNoticeSent) {
        concludedNoticeSent = true;
        callbacks.onClose();
      }
      backdrop.style.display = 'none';
      return;
    }
    // MFix3.4/5/6: reset the effect-tracking cursor when switching to a
    // different battle so we don't skip its initial events. Only fires
    // when currentBattle is null (fresh open) or has a different id.
    if (currentBattle === null || (currentBattle.id as unknown as number) !== (battle.id as unknown as number)) {
      resetEffectTracking(battle.id as unknown as string);
    }
    if (battle.result !== null && !concludedNoticeSent) {
      concludedNoticeSent = true;
      callbacks.onClose();
      backdrop.style.display = 'none';
      return;
    }
    currentBattle = battle;
    currentPlayerFactionId = playerFactionId;
    backdrop.style.display = 'flex';

    // Header text.
    const [aId, bId] = battle.armyIds;
    const armyA = state.armies.get(aId);
    const armyB = state.armies.get(bId);
    const nameA = armyA ? safeFactionName(state, armyA.ownerFactionId) : '?';
    const nameB = armyB ? safeFactionName(state, armyB.ownerFactionId) : '?';
    vs.textContent = `${nameA} vs ${nameB} — Round ${battle.round + 1}`;
    // v0M4.1.3 — 4-cell phase strip in the header with a per-phase progress
    // bar under the currently-active phase. Setup phase only rendered while
    // player has taken command (phase1EndTick active).
    updatePhaseStrip(phaseStrip, battle, state.tick);
    if (battle.phase1EndTick !== null) {
      const ticksLeft = Math.max(0, battle.phase1EndTick - state.tick);
      const secLeft = Math.ceil(ticksLeft / 5);
      phaseName.textContent = 'Setup';
      phaseTimer.textContent = `${secLeft}s to combat`;
    } else {
      // v0M4.1.3: per-phase countdown mirrors the sim dials (3s Movement,
      // 5s Combat, 2s Resolution). UI reads UI_PHASE_DURATIONS below; real
      // pacing is driven by battles.ts.
      const durationTicks = phaseDurationUiTicks(battle.currentPhase);
      const elapsed = Math.max(0, state.tick - battle.phaseStartTick);
      const ticksToNext = Math.max(0, durationTicks - elapsed);
      const secToNext = (ticksToNext / 5).toFixed(1);
      phaseName.textContent = phaseLabel(battle.currentPhase);
      phaseTimer.textContent = `Next phase in ${secToNext}s`;
    }

    drawGrid(canvas, battle, playerFactionId, selectedCohortIds, cellPx);
    // v0M4.1.1 — combat log sync.
    refreshCombatLog(battle);
    // v0M4.1.2 — Army stats now render in the right panel via updateArmyPanel.
    updateArmyPanel(armyPanelSection, battle, state, playerFactionId);

    // v0M4.0h.1 + h.2: phase-gate button disabling.
    // - Formation picker: enabled ONLY during Phase 1 (Setup) per v0M4.0c
    //   apply-commands gate. Frozen once combat starts.
    // - Commander's Orders: enabled ONLY during Phase 2 per v0M4.0d
    //   apply-commands gate.
    const inPhase1 = battle.phase1EndTick !== null && state.tick < battle.phase1EndTick;
    // MFix5+fix4 (2026-07-23): hide the entire formation section + End
    // Deployment button once Setup ends. They're inert post-Setup and
    // just take up left-panel space. Uses `display: none` on each
    // element rather than removing them from the DOM so re-showing on
    // future Setup phases (rare — a single battle only Setup-phases
    // once) is a display-flag toggle.
    formationLabel.style.display = inPhase1 ? '' : 'none';
    endDeploymentBtn.style.display = inPhase1 ? '' : 'none';
    // v0M4.4.1 — collapse the preview canvas too when Setup ends.
    previewCanvas.style.display = inPhase1 ? '' : 'none';
    // v0M9.14 (2026-07-24) — research gate. Look up the player faction
    // once per frame; gate formation buttons on both the phase-1 window
    // AND the knowledge unlock check per template.
    const playerFaction = playerFactionId !== null ? state.factions.get(playerFactionId) : null;
    for (const entry of formationButtons) {
      entry.btn.style.display = inPhase1 ? '' : 'none';
      const humanized = humanizeFormationName(entry.template);
      const unlocked = playerFaction
        ? isFormationUnlocked(humanized, playerFaction, FORMATION_UNLOCK_MAP)
        : true;
      if (!inPhase1) {
        entry.btn.disabled = true;
        entry.btn.title = 'Formation can only be picked during Setup.';
      } else if (!unlocked) {
        entry.btn.disabled = true;
        entry.btn.title = `Requires: ${FORMATION_UNLOCK_MAP.get(humanized)}`;
      } else {
        entry.btn.disabled = false;
        entry.btn.title = '';
      }
    }
    endDeploymentBtn.disabled = !inPhase1;
    for (const entry of commanderOrderButtons) {
      entry.btn.disabled = inPhase1;
      entry.btn.title = inPhase1 ? 'Commander orders take effect once battle begins.' : '';
    }
    updateRightPanel(
      rightHint,
      rightDetails,
      rightName,
      rightPos,
      rightMorale,
      rightStamina,
      rightFigures,
      rightTags,
      rightFacing,
      rightStandingOrder,
      rightStats,
      figureGrid, // MFix2.2 — 60-figure health-tint grid
      cohortOrderButtons,
      moveTargetLine,
      portraitIcon,
      portraitFallback,
      battle,
      playerFactionId,
      selectedCohortIds,
    );
    updateInsetResult(battle);
  }

  /**
   * Refresh the cohort inset row. Structure-builds only when the selected
   * cohort changes; per-frame updates only mutate colors + visibility.
   */
  function updateInsetResult(battle: Battle): void {
    // v0M4.0h.4: inset always shows the FIRST selected cohort's figures.
    // Multi-select doesn't multiply the inset — 60 figures per selected
    // would blow the row width.
    const primaryId = firstSelected();
    if (primaryId === null) {
      inset.style.display = 'none';
      return;
    }
    const cof = findCohortInBattle(battle, primaryId);
    if (!cof) {
      inset.style.display = 'none';
      return;
    }
    inset.style.display = '';

    // Structural rebuild: only when the selected cohort id changed.
    if (insetCohortId !== cof.id) {
      insetCohortId = cof.id;
      insetFigureDivs = [];
      // Bring the inset row into view. The modal is the scrollable
      // ancestor (overflow-y: auto). Schedule after the DOM update so
      // scroll math sees the just-populated formation grid.
      requestAnimationFrame(() => {
        inset.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
      // Clear children without breaking the pre-built title (title is a
      // separate child of `inset`, not `insetFormation`).
      while (insetFormation.firstChild) insetFormation.removeChild(insetFormation.firstChild);

      // Session-27b (2026-07-24): per-cohort front-rank width from
      // cof.stats.frontWidth (xlsx-authoritative). Retires the
      // isSiege ? 2 : COHORT_WIDTH hardcoded ternary.
      const cols = cof.stats.frontWidth;
      insetFormation.style.gridTemplateColumns = `repeat(${cols}, ${INSET_FIGURE_PX}px)`;
      insetFormation.style.gridAutoRows = `${INSET_FIGURE_PX}px`;

      const iconName = cohortIconName(cof);
      const url = `/icons/units/${iconName}.svg`;
      const initial = tagInitial(cof);

      for (let i = 0; i < cof.figures.length; i++) {
        const fig = document.createElement('div');
        fig.className = 'su-battle-inset-figure';
        fig.style.webkitMaskImage = `url("${url}")`;
        fig.style.maskImage = `url("${url}")`;
        // Pre-populate fallback text so if the SVG mask fails the shape is
        // still legible without a re-layout.
        fig.textContent = initial;
        fig.style.fontSize = `${Math.max(14, Math.floor(INSET_FIGURE_PX * 0.42))}px`;
        insetFormation.appendChild(fig);
        insetFigureDivs.push(fig);
      }

      // Probe icon availability once per cohort selection. If it fails,
      // strip the mask so the fallback letter renders in the clear.
      probeIcon(url).then((ok) => {
        if (insetCohortId !== cof.id) return; // selection changed under us
        if (!ok) {
          for (const div of insetFigureDivs) {
            div.classList.add('su-fallback');
          }
        }
      });
    }

    // Per-frame: title text + per-figure state.
    const total = cof.figures.length;
    let alive = 0;
    let wounded = 0;
    for (const f of cof.figures) {
      if (f.killed) continue;
      alive++;
      if (f.wounded) wounded++;
    }
    insetTitleName.textContent = tagName(cof);
    insetTitleCounts.textContent =
      wounded > 0
        ? ` — ${alive} / ${total} figures (${wounded} wounded)`
        : ` — ${alive} / ${total} figures`;

    const factionDef = safeFactionDef(cof.factionId);
    const [r, g, b] = factionDef ? factionDef.color : [180, 180, 180];
    // Wounded palette: darker + shifted toward red so wounded reads
    // visibly different from healthy at a glance.
    const wr = Math.min(255, Math.floor(r * 0.55 + 90));
    const wg = Math.floor(g * 0.35);
    const wb = Math.floor(b * 0.35);
    const healthyColor = `rgb(${r}, ${g}, ${b})`;
    const woundedColor = `rgb(${wr}, ${wg}, ${wb})`;

    for (let i = 0; i < insetFigureDivs.length; i++) {
      const div = insetFigureDivs[i]!;
      const f = cof.figures[i];
      if (!f || f.killed) {
        div.style.visibility = 'hidden';
        continue;
      }
      div.style.visibility = 'visible';
      if (f.wounded) {
        div.style.backgroundColor = woundedColor;
        div.style.opacity = '0.65';
      } else {
        div.style.backgroundColor = healthyColor;
        div.style.opacity = '1';
      }
    }
  }

  function destroy(): void {
    backdrop.remove();
  }

  /**
   * v0M4.1.1 — Combat log incremental render.
   * - Rebuilds if the open battle id changed (fresh battle → empty log start).
   * - Appends only new events since the last render, capping DOM rows at 200.
   * - Auto-scrolls to newest unless the user is hovering (pause on hover).
   */
  function refreshCombatLog(battle: Battle): void {
    const battleKey = battle.id as unknown as number;
    if (battleKey !== lastLoggedBattleId) {
      combatLogList.innerHTML = '';
      lastRenderedLogIndex = 0;
      lastLoggedBattleId = battleKey;
    }
    if (lastRenderedLogIndex >= battle.log.length) return;
    const scrollAtBottomBefore =
      combatLogList.scrollTop + combatLogList.clientHeight >= combatLogList.scrollHeight - 4;
    for (let i = lastRenderedLogIndex; i < battle.log.length; i++) {
      const event = battle.log[i]!;
      const row = renderLogRow(event, battle);
      combatLogList.appendChild(row);
    }
    lastRenderedLogIndex = battle.log.length;
    // DOM cap — drop oldest rows beyond 200 to prevent unbounded growth.
    while (combatLogList.children.length > 200) {
      combatLogList.removeChild(combatLogList.firstChild!);
    }
    if (!hoveringLog && (scrollAtBottomBefore || combatLogList.children.length < 30)) {
      combatLogList.scrollTop = combatLogList.scrollHeight;
    }
  }

  return { sync, destroy };
}

/**
 * MFix2.2 — 60-figure grid on the right panel with per-figure health tint.
 *
 * Layout: 12 wide × 5 deep for infantry (60 figures); 2 wide × 2 deep for
 * Siege (4 figures). Each figure renders as a small vertical bar:
 *  - Killed figures are hidden entirely (empty cell, transparent).
 *  - Alive figures render a vertical gradient:
 *      · Green from bottom = healthy portion (`figure.health / maxHealth`)
 *      · Damage colour from top = 1 − healthy
 *      · Damage colour = orange when health lost ≤ 50%, red when > 50%
 *        (per Michael's spec — >50% loss = wounded / unable to fight).
 *
 * Rebuilds the grid DOM whenever the cohort shape changes (siege vs
 * infantry, or figure-count delta from previous render). Otherwise
 * mutates individual figure background gradients in place — cheap.
 *
 * PLAYTEST TODO: swap the `div` placeholders for an SVG-mask + faction-
 * colour background pattern once Michael sources a generic soldier icon
 * from Game-Icons.net. The health-tint gradient math stays identical.
 */
function renderFigureGrid(grid: HTMLElement, cof: CohortOnField): void {
  // Session-27b (2026-07-24): per-cohort layout from cof.stats.frontWidth
  // (xlsx-authoritative col O); depth derived as figureCount / frontWidth.
  // Retires the isSiege ? 2 : 12×5 ternary.
  const cols = cof.stats.frontWidth;
  const rows = Math.max(1, Math.ceil(cof.stats.figureCount / cols));
  const slotCount = cols * rows;
  const maxHealth = cof.stats.maxHealthPerFigure;

  // Rebuild the grid DOM if the current child count doesn't match slotCount
  // (cohort type changed) OR the layout differs. Cheap to always check.
  const wantCells = slotCount;
  const havingCells = grid.children.length;
  if (havingCells !== wantCells || grid.dataset.cols !== String(cols)) {
    grid.innerHTML = '';
    grid.dataset.cols = String(cols);
    grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
    for (let i = 0; i < wantCells; i++) {
      const cell = document.createElement('div');
      cell.className = 'su-figure-cell';
      grid.appendChild(cell);
    }
  }

  // Session-28 (2026-07-24) — 3-colour health tint per Michael's playtest ask:
  //   killed          → red   rgb(196, 116, 116) at full opacity
  //   health < 50%    → yellow rgb(212, 200, 116)
  //   health ≥ 50%    → green  rgb(120, 168, 108)
  // Cell is a person-shaped CSS mask (see .su-figure-cell in menu-styles.ts);
  // background-color fills the person silhouette. If the cohort's figure
  // array is shorter than slotCount, trailing cells render as killed.
  for (let i = 0; i < wantCells; i++) {
    const cell = grid.children[i] as HTMLElement;
    const fig: import('../../sim/battle-state').Figure | undefined = cof.figures[i];
    if (!fig || fig.killed) {
      cell.style.backgroundColor = 'rgb(196, 116, 116)'; // red — dead
      cell.style.opacity = '1';
      continue;
    }
    const healthy = Math.max(0, Math.min(1, fig.health / maxHealth));
    cell.style.backgroundColor = healthy >= 0.5
      ? 'rgb(120, 168, 108)'  // green — healthy (>= 50%)
      : 'rgb(212, 200, 116)'; // yellow — wounded (< 50%)
    cell.style.opacity = '1';
  }
}

/**
 * v0M4.1.3 — Phase-strip helpers. Duration values MIRROR the sim dials
 * (BATTLES.md §2.9.4 lock: Movement 15 / Combat 25 / Resolution 10) so
 * the countdown shown matches when the sim actually transitions. Cosmetic
 * drift is acceptable if the dials change — real pacing is on the sim side.
 */
function phaseDurationUiTicks(phase: 'setup' | 'movement' | 'combat' | 'resolution'): number {
  switch (phase) {
    case 'movement':   return 15;
    case 'combat':     return 25;
    case 'resolution': return 10;
    case 'setup':      return 100; // phase-1 timer (~20s ≈ 100 ticks); overridden below when actually used.
  }
}

function phaseLabel(phase: 'setup' | 'movement' | 'combat' | 'resolution'): string {
  switch (phase) {
    case 'setup':      return 'Setup';
    case 'movement':   return 'Movement';
    case 'combat':     return 'Combat';
    case 'resolution': return 'Resolution';
  }
}

function updatePhaseStrip(
  strip: HTMLElement,
  battle: Battle,
  currentTick: number,
): void {
  const cells = strip.querySelectorAll<HTMLElement>('.su-phase-cell');
  const inSetup = battle.phase1EndTick !== null && currentTick < battle.phase1EndTick;
  const activePhase: 'setup' | 'movement' | 'combat' | 'resolution' = inSetup ? 'setup' : battle.currentPhase;
  cells.forEach((cell) => {
    const phase = cell.dataset.phase as 'setup' | 'movement' | 'combat' | 'resolution';
    if (phase === 'setup') {
      cell.style.display = inSetup ? '' : 'none';
    }
    cell.classList.toggle('su-phase-active', phase === activePhase);
    const fill = cell.querySelector<HTMLElement>('.su-phase-cell-bar-fill');
    if (!fill) return;
    if (phase === activePhase) {
      if (phase === 'setup' && battle.phase1EndTick !== null) {
        const total = Math.max(1, battle.phase1EndTick - battle.phaseStartTick);
        const pct = Math.max(0, Math.min(100, ((currentTick - battle.phaseStartTick) / total) * 100));
        fill.style.width = `${pct.toFixed(1)}%`;
      } else {
        const total = phaseDurationUiTicks(phase);
        const elapsed = currentTick - battle.phaseStartTick;
        const pct = Math.max(0, Math.min(100, (elapsed / total) * 100));
        fill.style.width = `${pct.toFixed(1)}%`;
      }
    } else {
      fill.style.width = '0%';
    }
  });
}

/**
 * v0M4.1.1 — Format a single combat-log event as a DOM row.
 * Severity class controls colour; icon prefix is a leading unicode glyph
 * (kept simple to avoid asset loads).
 */
function renderLogRow(event: CombatLogEvent, battle: Battle): HTMLElement {
  const row = document.createElement('div');
  row.className = 'su-combat-log-row';

  const iconSpan = document.createElement('span');
  iconSpan.className = 'su-combat-log-icon';

  const textSpan = document.createElement('span');
  textSpan.className = 'su-combat-log-text';

  const roundSpan = document.createElement('span');
  roundSpan.className = 'su-combat-log-round';
  roundSpan.textContent = `R${event.round + 1}`;

  const attackerName = (id: number): string => `#${id}`;
  const defenderName = attackerName;

  switch (event.kind) {
    case 'melee-strike': {
      iconSpan.textContent = event.chargeActive ? '⚔' : '×';
      const parts: string[] = [`${attackerName(event.attackerId as unknown as number)} melee → ${defenderName(event.defenderId as unknown as number)}`];
      if (event.killCount > 0) parts.push(`${event.killCount} killed`);
      if (event.woundedCount > 0) parts.push(`${event.woundedCount} wounded`);
      if (event.chargeActive) parts.push('CHARGE');
      textSpan.textContent = parts.join(', ');
      row.classList.add(event.chargeActive ? 'severity-charge' : (event.killCount > 0 ? 'severity-kill' : 'severity-morale'));
      break;
    }
    case 'missile-fire': {
      iconSpan.textContent = '➤';
      const parts: string[] = [`${attackerName(event.firerId as unknown as number)} shoots → ${defenderName(event.targetId as unknown as number)}`];
      if (event.killCount > 0) parts.push(`${event.killCount} killed`);
      if (event.woundedCount > 0) parts.push(`${event.woundedCount} wounded`);
      if (event.friendlyFireKills > 0) parts.push(`⚠ ${event.friendlyFireKills} friendly killed`);
      textSpan.textContent = parts.join(', ');
      row.classList.add(event.killCount > 0 ? 'severity-kill' : 'severity-morale');
      break;
    }
    case 'rout': {
      iconSpan.textContent = '⚑';
      textSpan.textContent = `${attackerName(event.cohortId as unknown as number)} ROUTED (${event.trigger})`;
      row.classList.add('severity-rout');
      break;
    }
    case 'formation-cascade': {
      iconSpan.textContent = '⊞';
      textSpan.textContent = `Formation: ${event.template} (Side ${event.sideIndex})`;
      row.classList.add('severity-order');
      break;
    }
    case 'commander-order': {
      iconSpan.textContent = '☰';
      textSpan.textContent = `Commander's Order: ${event.order} (Side ${event.sideIndex})`;
      row.classList.add('severity-order');
      break;
    }
    case 'battle-end': {
      iconSpan.textContent = '★';
      textSpan.textContent = `Battle end — ${event.result}`;
      row.classList.add('severity-battle-end');
      break;
    }
    case 'commander-xp': {
      iconSpan.textContent = event.newLevel !== null ? '☆' : '+';
      const promo = event.newLevel !== null ? ` → rank ${event.newLevel}` : '';
      textSpan.textContent = `Commander #${event.commanderId as unknown as number} +${event.xpDelta} XP${promo}`;
      row.classList.add('severity-xp');
      break;
    }
    case 'phase-transition': {
      iconSpan.textContent = '◷';
      textSpan.textContent = `Phase → ${event.toPhase}`;
      row.classList.add('severity-order');
      break;
    }
  }

  row.appendChild(iconSpan);
  row.appendChild(textSpan);
  row.appendChild(roundSpan);
  // Reference battle to silence unused-var warning if switch branches don't touch it.
  void battle;
  return row;
}

// ---------------------------------------------------------------------------
// Canvas rendering
// ---------------------------------------------------------------------------

function drawGrid(
  canvas: HTMLCanvasElement,
  battle: Battle,
  playerFactionId: FactionId | null,
  selectedCohortIds: ReadonlySet<CohortId>,
  cellPx: number,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const canvasW = BATTLESCAPE_WIDTH * cellPx;
  const canvasH = BATTLESCAPE_HEIGHT * cellPx;
  ctx.fillStyle = '#14100a';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // v0M4.0j.1 — Battlefield terrain layer. Paint per-cell terrain fill +
  // feature glyphs BEFORE the grid lines so the lines sit on top.
  drawTerrain(ctx, battle, cellPx);

  ctx.strokeStyle = '#2a2214';
  ctx.lineWidth = 1;
  for (let x = 0; x <= BATTLESCAPE_WIDTH; x++) {
    ctx.beginPath();
    ctx.moveTo(x * cellPx + 0.5, 0);
    ctx.lineTo(x * cellPx + 0.5, canvasH);
    ctx.stroke();
  }
  for (let y = 0; y <= BATTLESCAPE_HEIGHT; y++) {
    ctx.beginPath();
    ctx.moveTo(0, y * cellPx + 0.5);
    ctx.lineTo(canvasW, y * cellPx + 0.5);
    ctx.stroke();
  }
  // Deployment-zone tint.
  ctx.fillStyle = 'rgba(200, 150, 60, 0.06)';
  ctx.fillRect(0, 0, canvasW, 5 * cellPx);
  ctx.fillRect(0, 15 * cellPx, canvasW, 5 * cellPx);

  for (const side of battle.sides) {
    for (const cof of side.cohorts.values()) {
      if (cof.broken) continue;
      drawCohort(ctx, cof, playerFactionId, selectedCohortIds, cellPx);
    }
  }

  // MFix3.4/5/6: process new combat-log events (enqueue VFX + play SFX)
  // then render the current effect queue on top of cohorts. Effects
  // draw over the battlescape but under any UI overlays.
  processNewBattleEvents(battle);
  renderBattleEffects(ctx, cellPx);
}

/**
 * v0M4.0j.1 — Battlefield terrain layer. Per-cell fill from a fixed
 * palette + small feature glyphs (tree cluster / hill triangle / wavy
 * water / stone silhouette / crenellated fortification). Primitive
 * fidelity per BATTLES.md §2.8.6.6; sprite-atlas hook is future work.
 */
function drawTerrain(
  ctx: CanvasRenderingContext2D,
  battle: Battle,
  cellPx: number,
): void {
  const terrain = battle.terrain;
  const w = BATTLESCAPE_WIDTH;
  const h = BATTLESCAPE_HEIGHT;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const ord = terrain[y * w + x]!;
      const t = TERRAIN_TYPE_ORDER[ord]!;
      ctx.fillStyle = terrainFillColor(t);
      ctx.fillRect(x * cellPx, y * cellPx, cellPx, cellPx);
      // Feature glyph overlay per type.
      drawTerrainGlyph(ctx, t, x * cellPx, y * cellPx, cellPx);
    }
  }
}

/** Per-terrain-type fill colour per BATTLES.md §2.8.6.1 palette. */
function terrainFillColor(t: TerrainType): string {
  switch (t) {
    case 'Plains':          return '#3a4a2a';
    case 'Hills':          return '#6b5a3a';
    case 'Forest':         return '#25381f';
    case 'Swamp':          return '#4a5030';
    case 'Water':          return '#1e3a5a';
    case 'Mountains':      return '#3a3532';
    case 'Bridge':         return '#7a5a35';
    case 'Wall':  return '#6a6560';
    // v0M4.0k.8 (2026-07-27) — Desert + Gate palette. Desert = warm
    // sand; Gate = darker stone than Wall (the breach point / arch).
    case 'Desert':         return '#8a7550';
    case 'Gate':           return '#4a4540';
    default:               return '#3a4a2a';
  }
}

/**
 * Small primitive glyph inside a terrain cell. Uses proportional
 * dimensions so it scales with cellPx. Skips Grass (flat colour is
 * enough).
 */
function drawTerrainGlyph(
  ctx: CanvasRenderingContext2D,
  t: TerrainType,
  x: number,
  y: number,
  cellPx: number,
): void {
  const cx = x + cellPx / 2;
  const cy = y + cellPx / 2;
  ctx.save();
  switch (t) {
    case 'Hills': {
      // Simple triangle silhouette.
      ctx.fillStyle = 'rgba(140, 110, 70, 0.45)';
      ctx.beginPath();
      ctx.moveTo(cx, y + cellPx * 0.3);
      ctx.lineTo(x + cellPx * 0.25, y + cellPx * 0.75);
      ctx.lineTo(x + cellPx * 0.75, y + cellPx * 0.75);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'Forest': {
      // Three small dark dots — canopy.
      ctx.fillStyle = 'rgba(20, 40, 20, 0.7)';
      const r = Math.max(1, cellPx * 0.13);
      ctx.beginPath();
      ctx.arc(cx - cellPx * 0.18, cy - cellPx * 0.05, r, 0, Math.PI * 2);
      ctx.arc(cx + cellPx * 0.18, cy - cellPx * 0.05, r, 0, Math.PI * 2);
      ctx.arc(cx,                  cy + cellPx * 0.18, r, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'Swamp': {
      // Horizontal wavy lines.
      ctx.strokeStyle = 'rgba(20, 30, 15, 0.55)';
      ctx.lineWidth = Math.max(0.8, cellPx * 0.06);
      const yLine = y + cellPx * 0.6;
      ctx.beginPath();
      ctx.moveTo(x + cellPx * 0.15, yLine);
      ctx.quadraticCurveTo(cx, yLine - cellPx * 0.1, x + cellPx * 0.85, yLine);
      ctx.stroke();
      break;
    }
    case 'Water': {
      // Two wavy lines.
      ctx.strokeStyle = 'rgba(180, 200, 230, 0.55)';
      ctx.lineWidth = Math.max(0.8, cellPx * 0.06);
      ctx.beginPath();
      ctx.moveTo(x + cellPx * 0.15, y + cellPx * 0.4);
      ctx.quadraticCurveTo(cx, y + cellPx * 0.3, x + cellPx * 0.85, y + cellPx * 0.4);
      ctx.moveTo(x + cellPx * 0.15, y + cellPx * 0.7);
      ctx.quadraticCurveTo(cx, y + cellPx * 0.6, x + cellPx * 0.85, y + cellPx * 0.7);
      ctx.stroke();
      break;
    }
    case 'Mountains': {
      // Sharp peak silhouette.
      ctx.fillStyle = 'rgba(20, 15, 12, 0.7)';
      ctx.beginPath();
      ctx.moveTo(cx,                y + cellPx * 0.2);
      ctx.lineTo(x + cellPx * 0.15, y + cellPx * 0.85);
      ctx.lineTo(x + cellPx * 0.85, y + cellPx * 0.85);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'Bridge': {
      // Horizontal planks — three short lines across the cell.
      ctx.strokeStyle = 'rgba(50, 30, 15, 0.75)';
      ctx.lineWidth = Math.max(0.8, cellPx * 0.08);
      for (const ratio of [0.35, 0.55, 0.75]) {
        ctx.beginPath();
        ctx.moveTo(x + cellPx * 0.15, y + cellPx * ratio);
        ctx.lineTo(x + cellPx * 0.85, y + cellPx * ratio);
        ctx.stroke();
      }
      break;
    }
    case 'Wall': {
      // Crenellated top edge — three tiny squares.
      ctx.fillStyle = 'rgba(200, 190, 170, 0.55)';
      const s = cellPx * 0.15;
      ctx.fillRect(x + cellPx * 0.2, y + cellPx * 0.3, s, s);
      ctx.fillRect(x + cellPx * 0.425, y + cellPx * 0.3, s, s);
      ctx.fillRect(x + cellPx * 0.65, y + cellPx * 0.3, s, s);
      break;
    }
    case 'Gate': {
      // v0M4.0k.8 (2026-07-27) — Gate glyph: arched opening.
      // Invitingly walkable + visually distinct from walls. Two flanking
      // pillars + an arch spanning them.
      ctx.strokeStyle = 'rgba(220, 210, 180, 0.75)';
      ctx.lineWidth = Math.max(0.8, cellPx * 0.06);
      // Left pillar
      ctx.strokeRect(x + cellPx * 0.2, y + cellPx * 0.5, cellPx * 0.08, cellPx * 0.35);
      // Right pillar
      ctx.strokeRect(x + cellPx * 0.72, y + cellPx * 0.5, cellPx * 0.08, cellPx * 0.35);
      // Arch (semicircle top)
      ctx.beginPath();
      ctx.arc(cx, y + cellPx * 0.5, cellPx * 0.24, Math.PI, 0, false);
      ctx.stroke();
      break;
    }
    case 'Desert': {
      // v0M4.0k.8 (2026-07-27) — Desert glyph: sparse dune dots.
      ctx.fillStyle = 'rgba(200, 175, 130, 0.55)';
      const r = Math.max(1, cellPx * 0.09);
      ctx.beginPath();
      ctx.arc(cx - cellPx * 0.2, cy + cellPx * 0.15, r, 0, Math.PI * 2);
      ctx.arc(cx + cellPx * 0.15, cy - cellPx * 0.05, r, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'Plains':
    default:
      break;
  }
  ctx.restore();
}

/**
 * Draw a cohort at its cell position as a grid of figure dots
 * (v0M3.5+fix3). Each figure = one dot. Alive+healthy = full faction
 * color; wounded = 50% brightness; killed = gap. The 12×5 formation
 * layout matches §2.7.1 (Siege renders as a smaller 2×2 grid). Front
 * row lives at cohort.figures[0..11] and is drawn at the top-inner row
 * of the cell for consistent orientation regardless of facing (facing
 * is indicated by the shield outline and a small directional pip).
 */
function drawCohort(
  ctx: CanvasRenderingContext2D,
  cof: CohortOnField,
  playerFactionId: FactionId | null,
  selectedCohortIds: ReadonlySet<CohortId>,
  cellPx: number,
): void {
  const cellX = cof.position.x * cellPx;
  const cellY = cof.position.y * cellPx;
  const isPlayer = playerFactionId !== null && cof.factionId === playerFactionId;
  const isSelected = selectedCohortIds.has(cof.id);
  const factionDef = safeFactionDef(cof.factionId);
  const [r, g, b] = factionDef ? factionDef.color : [140, 140, 140];

  // Cohort background: faint faction tint so the cell area is legible
  // even when many figures are dead.
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.14)`;
  ctx.fillRect(cellX + 2, cellY + 2, cellPx - 4, cellPx - 4);

  // Figure dot grid. Session-27b (2026-07-24): per-cohort layout from
  // cof.stats.frontWidth (xlsx-authoritative col O); depth is derived
  // as figureCount / frontWidth. Retires the isSiege ? 2 : 12×5 ternary.
  const cols = cof.stats.frontWidth;
  const rows = Math.max(1, Math.ceil(cof.stats.figureCount / cols));
  // Leave 2px margin inside the cell + 2px at bottom for the morale bar.
  const INSET = 3;
  const MORALE_RESERVE = 3;
  const figureAreaW = cellPx - 2 * INSET;
  const figureAreaH = cellPx - 2 * INSET - MORALE_RESERVE;
  const cellW = figureAreaW / cols;
  const cellH = figureAreaH / rows;
  const dotSize = Math.max(1.5, Math.min(cellW, cellH) - 0.5);
  const dotOffsetX = cellX + INSET + (cellW - dotSize) / 2;
  const dotOffsetY = cellY + INSET + (cellH - dotSize) / 2;

  const fullColor = `rgb(${r}, ${g}, ${b})`;
  const woundedColor = `rgba(${r}, ${g}, ${b}, 0.5)`;

  const totalSlots = cols * rows;
  for (let idx = 0; idx < Math.min(cof.figures.length, totalSlots); idx++) {
    const fig = cof.figures[idx]!;
    if (fig.killed) continue;
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const dx = dotOffsetX + col * cellW;
    const dy = dotOffsetY + row * cellH;
    ctx.fillStyle = fig.wounded ? woundedColor : fullColor;
    ctx.fillRect(dx, dy, dotSize, dotSize);
  }

  // Cohort outline — selection > player > enemy.
  if (isSelected) {
    ctx.strokeStyle = '#ffdc84';
    ctx.lineWidth = 3;
  } else if (isPlayer) {
    ctx.strokeStyle = '#f5eacf';
    ctx.lineWidth = 2;
  } else {
    ctx.strokeStyle = '#4a3d24';
    ctx.lineWidth = 1.5;
  }
  ctx.strokeRect(cellX + 1.5, cellY + 1.5, cellPx - 3, cellPx - 3);

  // Facing pip — small triangle on the edge indicating cohort facing.
  drawFacingPip(ctx, cellX, cellY, cellPx, cof.facing.direction);

  // Morale bar (bottom edge of cell).
  const moralePct = Math.max(0, Math.min(1, cof.morale / 100));
  const barY = cellY + cellPx - MORALE_RESERVE;
  const barW = (cellPx - 6) * moralePct;
  ctx.fillStyle = moralePct > 0.5 ? '#a3c47a' : moralePct > 0.25 ? '#d4a874' : '#c47474';
  ctx.fillRect(cellX + 3, barY, barW, 2);
}

function drawFacingPip(
  ctx: CanvasRenderingContext2D,
  cellX: number,
  cellY: number,
  cellPx: number,
  facing: 'N' | 'S' | 'E' | 'W',
): void {
  const cx = cellX + cellPx / 2;
  const cy = cellY + cellPx / 2;
  const R = 3;
  ctx.fillStyle = '#f5eacf';
  ctx.strokeStyle = '#1a140a';
  ctx.lineWidth = 1;
  ctx.beginPath();
  switch (facing) {
    case 'N':
      ctx.moveTo(cx, cellY + 1);
      ctx.lineTo(cx - R, cellY + 1 + R);
      ctx.lineTo(cx + R, cellY + 1 + R);
      break;
    case 'S':
      ctx.moveTo(cx, cellY + cellPx - 1);
      ctx.lineTo(cx - R, cellY + cellPx - 1 - R);
      ctx.lineTo(cx + R, cellY + cellPx - 1 - R);
      break;
    case 'E':
      ctx.moveTo(cellX + cellPx - 1, cy);
      ctx.lineTo(cellX + cellPx - 1 - R, cy - R);
      ctx.lineTo(cellX + cellPx - 1 - R, cy + R);
      break;
    case 'W':
      ctx.moveTo(cellX + 1, cy);
      ctx.lineTo(cellX + 1 + R, cy - R);
      ctx.lineTo(cellX + 1 + R, cy + R);
      break;
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

// ---------------------------------------------------------------------------
// Panel updaters — mutate existing DOM only
// ---------------------------------------------------------------------------

/**
 * v0M4.1.2 — Army stats panel (BATTLES.md §2.9.3 top section). Renders
 * Commander name + rank + XP progress bar + cohort limit and aggregate
 * stats (cohort count, total figures, avg stamina, avg morale, broken
 * count) for the PLAYER's side. Enemy army stats are not shown here —
 * per Michael Q6 the panel is player-side, not enemy-side, at v0M4.1.2.
 */
function updateArmyPanel(
  section: HTMLElement,
  battle: Battle,
  state: GameState,
  playerFactionId: FactionId,
): void {
  const commanderLine = section.querySelector<HTMLElement>('[data-slot="commander"]');
  const xpLabel = section.querySelector<HTMLElement>('[data-slot="xp"]');
  const xpFill = section.querySelector<HTMLElement>('.su-army-xp-bar-fill');
  const aggregateLine = section.querySelector<HTMLElement>('[data-slot="aggregate"]');
  const rosterLine = section.querySelector<HTMLElement>('[data-slot="roster"]');
  if (!commanderLine || !xpLabel || !xpFill || !aggregateLine || !rosterLine) return;

  let playerSide: Side | null = null;
  for (const side of battle.sides) {
    for (const cof of side.cohorts.values()) {
      if (cof.factionId === playerFactionId) {
        playerSide = side;
        break;
      }
    }
    if (playerSide) break;
  }
  if (!playerSide) {
    commanderLine.textContent = 'Observing.';
    xpLabel.textContent = '';
    xpFill.style.width = '0%';
    aggregateLine.textContent = '';
    rosterLine.textContent = '';
    return;
  }
  const commander = state.commanders.get(playerSide.commanderId);
  if (commander) {
    const cohortLimit = Math.min(25, 5 + commander.level);
    commanderLine.textContent = `Commander lvl ${commander.level} — ${playerSide.cohorts.size}/${cohortLimit} cohorts`;
    const level = Math.min(20, commander.level);
    const xpIntoLevel = commander.xp - (level - 1) * 1000;
    const xpToNext = 1000;
    const pct = level >= 20 ? 100 : Math.max(0, Math.min(100, (xpIntoLevel / xpToNext) * 100));
    xpFill.style.width = `${pct.toFixed(1)}%`;
    xpLabel.textContent = level >= 20
      ? `XP ${commander.xp} — MAX RANK`
      : `XP ${xpIntoLevel}/${xpToNext} to rank ${level + 1}`;
  } else {
    commanderLine.textContent = 'Commander missing.';
    xpLabel.textContent = '';
    xpFill.style.width = '0%';
  }
  let alive = 0;
  let routed = 0;
  let exited = 0;
  let totalFigures = 0;
  let staminaSum = 0;
  let moraleSum = 0;
  let statSampleCount = 0;
  for (const cof of playerSide.cohorts.values()) {
    if (cof.broken) routed++;
    else if (cof.exited) exited++;
    else alive++;
    if (!cof.broken && !cof.exited) {
      totalFigures += cof.figures.filter((f) => !f.killed).length;
      staminaSum += cof.stamina;
      moraleSum += cof.morale;
      statSampleCount++;
    }
  }
  const avgStamina = statSampleCount > 0 ? Math.round(staminaSum / statSampleCount) : 0;
  const avgMorale = statSampleCount > 0 ? Math.round(moraleSum / statSampleCount) : 0;
  aggregateLine.textContent = `${alive} fighting · ${routed} routed${exited > 0 ? ` · ${exited} exited` : ''}`;
  rosterLine.textContent = `${totalFigures} figures · avg stamina ${avgStamina} · avg morale ${avgMorale}`;
}

function updateRightPanel(
  hint: HTMLElement,
  details: HTMLElement,
  nameEl: HTMLElement,
  posEl: HTMLElement,
  moraleEl: HTMLElement,
  staminaEl: HTMLElement,
  figuresEl: HTMLElement,
  tagsEl: HTMLElement,
  facingEl: HTMLElement,
  standingOrderEl: HTMLElement,
  statsEl: HTMLElement,
  figureGridEl: HTMLElement, // MFix2.2 — 60-figure health-tint grid
  cohortOrderButtons: ReadonlyArray<{ btn: HTMLButtonElement; spec: { readonly label: string; readonly order: StandingOrder; readonly tag: 'universal' | 'melee' | 'ranged' } }>,
  moveTargetLine: HTMLElement,
  portraitIcon: HTMLElement,
  portraitFallback: HTMLElement,
  battle: Battle,
  playerFactionId: FactionId,
  selectedCohortIds: ReadonlySet<CohortId>,
): void {
  if (selectedCohortIds.size === 0) {
    hint.style.display = '';
    details.style.display = 'none';
    return;
  }
  // v0M4.0h.4: multi-select — show first-selected's details; if multi,
  // append "+ N more" badge to the name.
  let firstId: CohortId | null = null;
  for (const id of selectedCohortIds) { firstId = id; break; }
  if (firstId === null) {
    hint.style.display = '';
    details.style.display = 'none';
    return;
  }
  const cof = findCohortInBattle(battle, firstId);
  if (!cof) {
    hint.style.display = '';
    details.style.display = 'none';
    return;
  }
  hint.style.display = 'none';
  details.style.display = '';
  const extraCount = selectedCohortIds.size - 1;
  nameEl.textContent = extraCount > 0
    ? `${tagName(cof)} (+${extraCount} more)`
    : tagName(cof);
  // MFix2.2 — replace the large portrait with a 60-figure health-tint
  // grid. renderFigureGrid rebuilds the grid DOM to match the current
  // cohort shape (12×5 for infantry, 2×2 for Siege) and colours each
  // figure by its per-figure health remaining. The old portrait DOM
  // (portrait / portraitIcon / portraitFallback) remains referenced
  // as offscreen sentinels so this signature doesn't churn.
  renderFigureGrid(figureGridEl, cof);
  // Suppress unused-var warnings for the retained portrait references.
  void portraitIcon; void portraitFallback;
  const isOwned = cof.factionId === playerFactionId;
  // v0M4.1.2 — "(Enemy)" suffix on the name for opposing cohorts so the
  // player can tell at a glance whose stats they're reading.
  if (!isOwned) {
    nameEl.textContent = `${nameEl.textContent} — Enemy`;
  }
  posEl.textContent = `Position: (${cof.position.x}, ${cof.position.y})`;
  moraleEl.textContent = `Morale: ${Math.round(cof.morale)} / 100`;
  staminaEl.textContent = `Stamina: ${Math.round(cof.stamina)} / 100`;
  const aliveFigures = cof.figures.filter((f) => !f.killed).length;
  const woundedFigures = cof.figures.filter((f) => f.wounded && !f.killed).length;
  const totalFigures = cof.figures.length;
  figuresEl.textContent = `Figures: ${aliveFigures}/${totalFigures}${woundedFigures > 0 ? ` (${woundedFigures} wounded)` : ''}`;
  // v0M4.1.2 — extended stat block.
  tagsEl.textContent = `Tags: ${cof.tags.join(', ') || '—'}`;
  facingEl.textContent = `Facing: ${cof.facing.direction}`;
  standingOrderEl.textContent = `Order: ${cof.standingOrder}`;
  // Balance-sheet stats — render as a compact grid.
  const s = cof.stats;
  const hasRangedTag = cof.tags.includes('Ranged');
  const rows: string[] = [
    `Attack ${s.attack}`,
    `Defence ${s.defence}`,
    `Armor ${s.armor}`,
    `Initiative ${s.initiative}`,
    `Move ${s.moveSpeedCombat}/round`,
  ];
  if (s.chargeBonus > 0) rows.push(`Charge +${s.chargeBonus}`);
  if (hasRangedTag && s.missileAttack > 0) {
    rows.push(`Missile ${s.missileAttack} (range ${s.missileRange})`);
  }
  statsEl.innerHTML = rows.map((r) => `<div>${r}</div>`).join('');
  // v0M4.0h.3: tag-conditional button visibility + active highlight per
  // the selected cohort's current standingOrder.
  const hasMelee = cof.tags.includes('Melee');
  const hasRanged = cof.tags.includes('Ranged');
  for (const entry of cohortOrderButtons) {
    const visible =
      entry.spec.tag === 'universal' ||
      (entry.spec.tag === 'melee' && hasMelee) ||
      (entry.spec.tag === 'ranged' && hasRanged);
    entry.btn.style.display = visible ? '' : 'none';
    entry.btn.disabled = !isOwned;
    entry.btn.className =
      cof.standingOrder === entry.spec.order
        ? 'su-battle-order-btn su-active'
        : 'su-battle-order-btn';
  }
  if (cof.moveOrder) {
    moveTargetLine.textContent = `Move target: (${cof.moveOrder.targetSquare.x}, ${cof.moveOrder.targetSquare.y})`;
    moveTargetLine.style.display = '';
  } else {
    moveTargetLine.textContent = 'Right-click a square to set the move target.';
    moveTargetLine.style.color = '#a3906a';
    moveTargetLine.style.display = '';
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function findCohortInBattle(battle: Battle, id: CohortId): CohortOnField | null {
  for (const side of battle.sides) {
    const cof = side.cohorts.get(id);
    if (cof) return cof;
  }
  return null;
}

function safeFactionName(state: GameState, factionId: FactionId): string {
  const f = state.factions.get(factionId);
  if (!f) return `Faction ${factionId as unknown as number}`;
  try {
    return getFactionById(factionId as unknown as number).name;
  } catch {
    return `Team ${f.teamId}`;
  }
}

function safeFactionDef(factionId: FactionId): { color: readonly [number, number, number]; name: string } | null {
  try {
    return getFactionById(factionId as unknown as number);
  } catch {
    return null;
  }
}

function tagName(cof: CohortOnField): string {
  // MFix6.2 (2026-07-23) — prefer the cohort's canonical Balance.xlsx
  // type name (populated on CohortOnField at hydration). Falls back to
  // the old tag-sniffing path only for cohorts with an unrecognised
  // type string (should never happen in v1).
  if (cof.type) return cof.type;
  if (cof.tags.includes('Ranged')) return 'Archer';
  if (cof.tags.includes('Siege')) return 'Catapult';
  return 'Swordsman';
}

/**
 * Map a cohort to its Game-Icons.net icon file basename. Falls back to
 * generic type icons; v0M3.5c ships icons for the 6 v1 cohort types.
 */
function cohortIconName(cof: CohortOnField): string {
  if (cof.tags.includes('Ranged')) return 'archer';
  if (cof.tags.includes('Siege')) return 'catapult';
  if (cof.tags.includes('Charge Resistance')) return 'spearman';
  // No clean tag for Light vs Shock cavalry vs Swordsman at v0M3.5.
  // Refine when cohort.type is available on CohortOnField (deferred).
  return 'swordsman';
}

function tagInitial(cof: CohortOnField): string {
  if (cof.tags.includes('Ranged')) return 'A';
  if (cof.tags.includes('Siege')) return 'C';
  return 'S';
}

/**
 * Cheap check: does the SVG file at `url` exist? Uses `Image()` so the
 * browser can cache the result. Returns `true` on load, `false` on
 * error. Resolves the first time called per url (subsequent calls hit
 * the browser cache; almost synchronous).
 */
const iconProbeCache = new Map<string, Promise<boolean>>();
function probeIcon(url: string): Promise<boolean> {
  const cached = iconProbeCache.get(url);
  if (cached) return cached;
  const p = new Promise<boolean>((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
  iconProbeCache.set(url, p);
  return p;
}
